import { useEffect, useState } from 'react';
import { AlertTriangle, Undo2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';

export interface VendaParaEstorno {
  id: string;
  valorTotal: string;
  createdAt: Date | string;
  itens: { produtoNome: string; codigo: string; quantidade: number; subtotal: string }[];
}

interface EstornarVendaModalProps {
  venda: VendaParaEstorno | null;
  onClose: () => void;
}

const MOTIVO_MINIMO = 3;

export function EstornarVendaModal({ venda, onClose }: EstornarVendaModalProps) {
  const [motivo, setMotivo] = useState('');
  const utils = trpc.useUtils();

  const estornarMutation = trpc.vendas.cancelar.useMutation({
    onSuccess: () => {
      utils.relatorios.invalidate();
      utils.produtos.list.invalidate();
      utils.caixas.invalidate();
      utils.dashboard.invalidate();
      onClose();
    },
  });

  useEffect(() => {
    if (venda) {
      setMotivo('');
      estornarMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venda?.id]);

  const moeda = (valor: string | number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
      typeof valor === 'string' ? Number.parseFloat(valor) : valor
    );

  // Venda de outro dia quase certamente está num caixa já conferido.
  const deOutroDia = venda
    ? new Date(venda.createdAt).toDateString() !== new Date().toDateString()
    : false;

  return (
    <Modal isOpen={Boolean(venda)} onClose={onClose} title="Estornar venda">
      <div className="space-y-4">
        <div className="rounded-lg bg-gray-50 p-4">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-gray-500">
              {venda && new Date(venda.createdAt).toLocaleString('pt-BR')}
            </span>
            <span className="text-xl font-bold text-gray-900">
              {venda && moeda(venda.valorTotal)}
            </span>
          </div>
          <ul className="mt-2 space-y-0.5 text-sm text-gray-600">
            {venda?.itens.map((item, i) => (
              <li key={i} className="flex justify-between">
                <span>
                  {item.quantidade}× {item.produtoNome}
                </span>
                <span>{moeda(item.subtotal)}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            As peças voltam para o estoque e o valor sai das somas do período.
            {deOutroDia && (
              <>
                {' '}
                Esta venda é de outro dia: o caixa dela provavelmente já foi conferido e{' '}
                <strong>o valor de fechamento não muda</strong> — o estorno aparece como ajuste
                posterior.
              </>
            )}
          </span>
        </div>

        <div>
          <label htmlFor="motivo-estorno" className="mb-1 block text-sm font-medium text-gray-700">
            Motivo do estorno
          </label>
          <textarea
            id="motivo-estorno"
            rows={3}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ex.: cliente desistiu da compra, peça com defeito, venda lançada em duplicidade"
            className="focus:border-primary-500 focus:ring-primary-500 w-full rounded-lg border border-gray-300 p-2 text-sm focus:outline-none focus:ring-1"
          />
          <p className="mt-1 text-xs text-gray-500">
            Fica registrado com seu nome e a data — é o que explica a diferença na conferência.
          </p>
        </div>

        {estornarMutation.isError && (
          <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{estornarMutation.error.message}</span>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={estornarMutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            isLoading={estornarMutation.isPending}
            disabled={motivo.trim().length < MOTIVO_MINIMO}
            onClick={() =>
              venda && estornarMutation.mutate({ vendaId: venda.id, motivo: motivo.trim() })
            }
          >
            <Undo2 className="mr-2 h-4 w-4" />
            Estornar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
